/**
 * Social API Types
 * Types for friends and groups functionality
 */

// Friend status enums
export enum FriendStatus {
  Pending = 0,
  Accepted = 1,
  Declined = 2,
  Blocked = 3,
}

export enum UserFacingFriendStatus {
  NotConnected = 0,
  Pending = 1,
  Connected = 2,
  Blocked = 3,
}

// Friend types
export interface FriendDto {
  id: string
  requesterUserId: string
  receiverUserId: string
  status: FriendStatus
  userFacingStatus: UserFacingFriendStatus
  requestDate: string
  responseDate?: string
  requestedByCurrentUser: boolean
  requesterName?: string
  receiverName?: string
  // Extended user info (may be populated by backend)
  userId?: string
  displayName?: string
  avatar?: string
  username?: string
  addedAt?: string
  mutualFriends?: number
  bio?: string
  location?: string
  stats?: {
    adventuresCompleted?: number
    trailsHiked?: number
    totalDistance?: number
  }
}

export interface FriendRequestDto {
  requestId: string
  userId: string
  displayName: string
  avatar?: string
  username: string
  requestedAt: string
  mutualFriends?: number
  bio?: string
}

// The API returns a flat array of FriendDto, which we need to filter client-side
export type FriendsResponse = FriendDto[]

export interface AddFriendRequest {
  friendId: string
}

export interface BlockUserRequest {
  userId: string
}

// Group visibility enum matching backend
export enum GroupVisibility {
  Public = 0,              // Anyone can see and join the group
  MatchingTagRequired = 1, // Only users with matching tags can see and join
  InviteOnly = 2          // Group is private, invitation required to join
}

// Group permission level enum (hierarchical)
export enum GroupPermissionLevel {
  Anyone = 0,          // No restrictions - anyone can perform this action
  MatchingTags = 1,    // Only users with matching interest tags can perform this action
  Members = 2,         // Only group members can perform this action
  Moderators = 3,      // Moderators and above (Moderator, Admin, Owner)
  Admins = 4,          // Admins and above (Admin, Owner)
  Owner = 5            // Only the group owner
}

// GroupPermissionType lives in @api/endpoints/groupRanks, next to the API that
// speaks it. The copy that used to be here was unused and had drifted: its
// ViewEvents = 6 and CreateEvents = 7 are the server's chat and adventure-transfer
// permissions, so anything that had imported it would have asked the wrong question
// and been answered without complaint.

// Helper to get visibility display name
export function getGroupVisibilityName(visibility: GroupVisibility): string {
  switch (visibility) {
    case GroupVisibility.Public:
      return 'Public'
    case GroupVisibility.MatchingTagRequired:
      return 'Matching Tag Required'
    case GroupVisibility.InviteOnly:
      return 'Invite Only'
    default:
      return 'Public'
  }
}

// Helper to get visibility description
export function getGroupVisibilityDescription(visibility: GroupVisibility): string {
  switch (visibility) {
    case GroupVisibility.Public:
      return 'Anyone can see and join the group'
    case GroupVisibility.MatchingTagRequired:
      return 'Only users with matching interest tags can see and join the group'
    case GroupVisibility.InviteOnly:
      return 'Group is private, only visible to members, invitation required to join'
    default:
      return 'Anyone can see and join the group'
  }
}

// Group role enum matching backend
/**
 * Structural standing in a group — not a rank.
 *
 * Owner and Admin pass every permission so a group cannot lock itself out of being administered;
 * everything else comes from the member's rank. Moderator sat at 1 and is gone: it said what a
 * rank says, and running both meant permissions granted to a rank could be silently overruled by
 * a role comparison.
 *
 * The values are pinned and 1 is left empty on purpose. They go over the wire as integers, so
 * renumbering would turn every stored Admin into an Owner.
 */
export enum GroupRole {
  Member = 0,
  Admin = 2,
  Owner = 3,
}

// Helper to get role display name
export function getGroupRoleName(role: GroupRole): string {
  switch (role) {
    case GroupRole.Member:
      return 'Member'
    case GroupRole.Admin:
      return 'Admin'
    case GroupRole.Owner:
      return 'Owner'
    default:
      return 'Member'
  }
}

// Join request status (numeric enum to match API)
export enum JoinRequestStatus {
  Pending = 0,
  Approved = 1,
  Rejected = 2,
  Cancelled = 3,
}

// Group join request DTO
export interface GroupJoinRequestDto {
  id: string
  groupId: string
  groupName?: string
  userId: string
  userName: string
  displayName?: string
  userAvatar?: string | null
  message?: string | null
  status: JoinRequestStatus
  requestedDate: string
  reviewedDate?: string | null
  reviewedBy?: string | null
  reviewNotes?: string | null
}

// Group banned user DTO
export interface GroupBannedUserDto {
  groupId: string
  userId: string | null
  userName: string | null
  userAvatar: string | null
  bannedDate: string | null
  bannedBy: string | null
}

// User group block DTO (for blocking groups from sending invites)
export interface UserGroupBlockDto {
  userId: string | null
  groupId: string
  groupName: string | null
  blockedDate: string
}

// Group types
export interface GroupMemberDto {
  groupId: string
  userId: string
  displayName: string
  userAvatar?: string // API returns userAvatar, not avatar
  userName?: string // API returns userName, not username
  hometown?: string
  reputation?: number
  role: GroupRole
  joinedDate: string // API returns joinedDate, not joinedAt
}

// Group permission settings DTO
export interface GroupPermissionSettingsDto {
  id: string
  groupId: string
  viewMembersLevel: GroupPermissionLevel
  inviteUsersLevel: GroupPermissionLevel
  viewTagsLevel: GroupPermissionLevel
  createDiscussionsLevel: GroupPermissionLevel
  participateInDiscussionsLevel: GroupPermissionLevel
  viewDiscussionsLevel: GroupPermissionLevel
  accessChatLevel: GroupPermissionLevel
  viewEventsLevel?: GroupPermissionLevel
  createEventsLevel?: GroupPermissionLevel
  createdAt: string
  updatedAt?: string
  updatedByUserId?: string
}

export interface GroupDto {
  id: string
  name: string
  description?: string
  fullDescription?: string
  groupImage?: string
  isPublic: boolean // Deprecated - use visibility instead
  visibility?: GroupVisibility // New field replacing isPublic
  maxMembers?: number
  creatorId?: string
  creatorName?: string
  createdDate: string // API returns createdDate, not createdAt
  memberCount: number
  isUserMember: boolean
  userRole?: GroupRole // API returns userRole (enum value)
  isOwner: boolean
  expirationDate?: string
  permissionSettings?: GroupPermissionSettingsDto // NEW: Permission settings
  geofenceCenterLatitude?: number | null
  geofenceCenterLongitude?: number | null
  geofenceRadiusMiles?: number | null
  canAccessChat?: boolean
  /** My own choice for showing this group on my profile (0 only me, 1 buddies, 4 everyone); null = my account setting. */
  myProfileVisibility?: number | null
}

export interface GroupTagDto {
  id: string
  name: string
  displayName: string
  categoryName: string
  categoryDisplayName: string
  icon?: string
  description?: string
  color?: string
}

export interface GroupDetailsDto extends GroupDto {
  members: GroupMemberDto[]
  interestTags?: GroupTagDto[]
  adventureTypeTags?: GroupTagDto[]
  shareCode?: string | null
  shareCodeExpirationDate?: string | null
  shareCodeMaxUses?: number | null
  shareCodeUsedCount?: number
  isArchived?: boolean
  archivedDate?: string | null
}

export interface GenerateShareCodeRequest {
  maxUses?: number | null
  expirationDate?: string | null
}

export interface ShareCodeResponse {
  shareCode: string
  expirationDate?: string | null
  maxUses?: number | null
  usedCount: number
}

export interface CreateGroupRequest {
  name: string
  description?: string
  fullDescription?: string
  isPublic?: boolean // Deprecated - use visibility instead
  visibility?: GroupVisibility // New field replacing isPublic
  maxMembers?: number
  groupImage?: string
  interestTagIds?: string[]
  adventureTypeTagIds?: string[]
  expirationDate?: string
  permissionSettings?: {
    viewMembersLevel: GroupPermissionLevel
    inviteUsersLevel: GroupPermissionLevel
    viewTagsLevel: GroupPermissionLevel
    createDiscussionsLevel: GroupPermissionLevel
    participateInDiscussionsLevel: GroupPermissionLevel
    viewDiscussionsLevel: GroupPermissionLevel
    accessChatLevel: GroupPermissionLevel
    viewEventsLevel?: GroupPermissionLevel
    createEventsLevel?: GroupPermissionLevel
  }
  geofenceCenterLatitude?: number | null
  geofenceCenterLongitude?: number | null
  geofenceRadiusMiles?: number | null
}

export interface UpdateGroupRequest {
  name?: string
  description?: string
  fullDescription?: string
  isPublic?: boolean // Deprecated - use visibility instead
  visibility?: GroupVisibility // New field replacing isPublic
  maxMembers?: number
  groupImage?: string
  interestTagIds?: string[]
  adventureTypeTagIds?: string[]
  expirationDate?: string
  permissionSettings?: {
    viewMembersLevel: GroupPermissionLevel
    inviteUsersLevel: GroupPermissionLevel
    viewTagsLevel: GroupPermissionLevel
    createDiscussionsLevel: GroupPermissionLevel
    participateInDiscussionsLevel: GroupPermissionLevel
    viewDiscussionsLevel: GroupPermissionLevel
    accessChatLevel: GroupPermissionLevel
    viewEventsLevel?: GroupPermissionLevel
    createEventsLevel?: GroupPermissionLevel
  }
  geofenceCenterLatitude?: number | null
  geofenceCenterLongitude?: number | null
  geofenceRadiusMiles?: number | null
}

export interface AddGroupMemberRequest {
  userId: string
  role?: GroupRole
}

export interface UpdateMemberRoleRequest {
  role: string // API expects "Member" | "Moderator" | "Admin" | "Owner"
}

export interface UpdateGroupPermissionsRequest {
  viewMembersLevel: GroupPermissionLevel
  inviteUsersLevel: GroupPermissionLevel
  viewTagsLevel: GroupPermissionLevel
  createDiscussionsLevel: GroupPermissionLevel
  participateInDiscussionsLevel: GroupPermissionLevel
  viewDiscussionsLevel: GroupPermissionLevel
  accessChatLevel: GroupPermissionLevel
  viewEventsLevel?: GroupPermissionLevel
  createEventsLevel?: GroupPermissionLevel
}

// ====================
// DISCUSSIONS & POSTS
// ====================

export interface DiscussionDto {
  id: string
  title: string
  content: string
  creatorId: string
  creatorName: string
  creatorAvatar?: string
  groupId: string
  groupName: string
  adventureId: string | null
  adventureName: string | null
  adventureCollectionId: string | null
  adventureCollectionName: string | null
  /** Set instead of groupId/adventureId for a profile blog post. */
  profileUserId?: string | null
  isLocked: boolean
  isPinned: boolean
  createdDate: string
  lastUpdated: string
  /** Replies, deleted ones left out. */
  postCount: number
  /**
   * Whether you may reply right now (allowed, and replies are on). Sent on a Blog list and on a
   * single discussion; null on group and adventure lists, whose hosts work it out themselves.
   */
  canReply?: boolean | null
  /** Whether you may turn replies off and on: the Blog post's owner, or a moderator. */
  canLock?: boolean
  imageId?: string
  /** The first image, for code written before a discussion could open with several. */
  imageUrl?: string
  /** Every image on the opening post, in order. Includes the one in imageUrl. */
  imageUrls?: string[]
}

/** GET /api/discussions/{id}/subscription */
export interface DiscussionSubscriptionDto {
  isSubscribed: boolean
}

export interface PostDto {
  id: string
  discussionId: string
  parentPostId?: string
  content: string
  authorId: string
  authorName: string
  authorAvatar?: string
  imageId?: string
  /** The first image, for code written before a post could carry several. */
  imageUrl?: string
  /** Every image on the post, in order. Includes the one in imageUrl. */
  imageUrls?: string[]
  createdDate: string
  updatedDate: string | null
  isEdited: boolean
  replyCount: number
  replies: PostDto[]
  // Moderation fields
  isHidden: boolean
  hiddenReason?: string
  isMasked: boolean
  hiddenByUserId?: string
  hiddenByDisplayName?: string
  hiddenDate?: string
  depth?: number
  /** Server-computed: you wrote it. Also how a client tells your own post from somebody else's. */
  canEdit?: boolean
  /** Server-computed: its author, a group admin or owner, or the Blog post's owner. */
  canDelete?: boolean
  /**
   * Deleted, but kept because others replied under it. Content, images and author are withheld;
   * show a "deleted" line in its place.
   */
  isDeleted?: boolean
}

/** PUT /api/discussions/{id}/locked */
export interface SetDiscussionLockedRequest {
  isLocked: boolean
}

export interface CreateDiscussionRequest {
  groupId?: string
  adventureId?: string
  adventureCollectionId?: string
  profileUserId?: string
  title: string
  content: string
  permissionLevel?: number
  isPinned?: boolean
  /** A single image, as older callers send it. Ignored when imageIds is present. */
  imageId?: string
  /** Images to attach, in the order they should read. The server caps this at 8. */
  imageIds?: string[]
}

export interface CreatePostRequest {
  discussionId: string
  parentPostId?: string
  content: string
  /** A single image, as older callers send it. Ignored when imageIds is present. */
  imageId?: string
  /** Images to attach, in the order they should read. The server caps this at 8. */
  imageIds?: string[]
}

export interface UpdatePostRequest {
  content: string
}

export interface UploadDiscussionImageResponse {
  imageId: string
  imageUrl: string
  fileSizeBytes: number
  width: number
  height: number
}

export interface FlagPostRequest {
  message: string
  reportTags: string[]
  reportToGroupModerators: boolean
}

// ====================
// SHARE CODE VALIDATION
// ====================

export enum ShareCodeAction {
  Register = 'Register',
  JoinAdventure = 'JoinAdventure',
  UnlockLocation = 'UnlockLocation',
  JoinGroup = 'JoinGroup',
}

export interface ShareCodeValidationResponse {
  isValid: boolean
  action: ShareCodeAction
  codeType: string
  targetId?: string | null
  targetName?: string | null
  message?: string | null
  requiresPermission: boolean
  userHasAccess: boolean
  unmetCriteria: string[]
  expiresAt?: string | null
  remainingUses?: number | null
}

// New sharecode processing types
export type ShareCodeProcessAction =
  | 'AuthenticationRequired'
  | 'GeocacheComplete'
  | 'AlreadyCompleted'
  | 'RequirementFulfilled'
  | 'WrongLocation'
  | 'DiscoveryOnly'
  | 'RewardCodeValid'
  | 'InvalidCode'

export type ShareCodeTargetType = 'Adventure' | 'Cache' | 'Referral' | 'Group' | 'User'

export type ShareCodeType = 'Reward' | 'User' | 'Adventure' | 'Legacy'

export interface RewardInfo {
  instanceId: string
  templateName: string
  description?: string
  remainingUses: number | null
  expiresAt: string | null
  isExpired?: boolean
  canOverrideExpiration?: boolean
  ownerDisplayName?: string
}

export interface ShareCodeProcessResponse {
  success: boolean
  action?: ShareCodeProcessAction
  message?: string
  // Code type identifier
  codeType?: ShareCodeType
  // Geocache/Adventure fields
  adventureId?: string
  adventureName?: string
  sessionId?: string
  creatorReferralCode?: string
  // Workflow fields
  locationId?: string
  locationName?: string
  requirementId?: string
  // Discovery fields
  targetId?: string
  targetType?: ShareCodeTargetType
  targetName?: string
  redirectUrl?: string
  // Reward fields
  canRedeem?: boolean
  rewardInfo?: RewardInfo
  // Error fields
  requiresAdditionalCriteria?: boolean
  shortCode?: string
}

export interface ShareCodeResponseDto {
  shareCode: string
  maxUses: number | null
  expirationDate: string | null
  usedCount: number
  createdAt: string
  creatorId: string
  creatorName: string
}

// ====================
// GROUP SEARCH
// ====================

export enum GroupType {
  Master = 0,
  Temporary = 1,
}

export interface GroupSearchRequest {
  query?: string
  visibility?: GroupVisibility
  groupType?: GroupType
  interestTagIds?: string[]
  adventureTypeTagIds?: string[]
  latitude?: number
  longitude?: number
  radiusMiles?: number
  withinGeofence?: boolean
  excludeJoined?: boolean
  includeArchived?: boolean
  /**
   * 'matchingTags' ranks by how many of `interestTagIds` a group shares, ties broken on member
   * count. Send it with those tags — on its own every group ties at zero and it degrades to
   * member count.
   */
  sortBy?: 'name' | 'createdDate' | 'memberCount' | 'matchingTags'
  sortDescending?: boolean
  skip?: number
  take?: number
}

export interface GroupSearchResponse {
  groups: GroupDto[]
  totalCount: number
  skip: number
  take: number
  hasMore: boolean
}

/** An invite this group has sent that has not yet been accepted or declined. */
export interface GroupInviteDto {
  groupId: string
  userId: string
  displayName: string
  userAvatar?: string
  invitedDate: string
  invitedBy: string
}


/**
 * One member's notification switches for one group.
 *
 * Mute is deliberately separate from turning all three off: it is a temporary "not now" that
 * must not lose which categories somebody had chosen, so unmuting restores what they had
 * rather than resubscribing them to everything.
 *
 * Group chat is not here. A conversation carries its own per-member notification mode, and a
 * second switch over the same thing is how the two end up disagreeing about who is muted.
 */
export interface GroupNotificationSettingsDto {
  isMuted: boolean
  /** A new discussion was started. Replies follow the per-discussion subscription instead. */
  discussions: boolean
  events: boolean
  /** Something added to the to-do list, or a date proposed on one. */
  todos: boolean
}
